import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Alert } from '@heroui/react';
import { api } from '@/lib/api';
import { useDataStore } from '@/stores/dataStore';
import { humanizeApiError } from '@/lib/humanizeApiError';
import { toast } from '@/lib/toast';
import { Button } from '@/components/Button';

interface InvitePreview {
  code: string;
  guild: { id: string; name: string; iconUrl: string | null };
  expiresAt: string | null;
  uses: number;
  maxUses: number | null;
  memberCount: number;
}

/**
 * Confirmation modal shown when a user lands on `/invite/<code>`.
 *
 * Replaces the old silent auto-accept: the user now sees which server they
 * are about to join, how many members are already there, and explicitly
 * chooses Join or Decline. On join we hydrate the guild into the store and
 * select it *before* closing, so the server rail updates immediately instead
 * of waiting on the (sometimes late) `guild:join` socket event.
 */
export function InviteAcceptModal(props: {
  code: string;
  /** Called after the user joins, declines, or the invite is invalid. */
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .previewInvite(props.code)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (cancelled) return;
        const h = humanizeApiError(err, t('srv.inviteInvalid'));
        setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.code, t]);

  async function join() {
    if (joining) return;
    setJoining(true);
    setError(null);
    let guildId: string;
    try {
      guildId = (await api.acceptInvite(props.code)).guildId;
    } catch (err) {
      const h = humanizeApiError(err, t('srv.joinFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
      setJoining(false);
      return;
    }
    // Joined. Hydrate + select before closing so the rail shows the server at
    // once; if that fetch fails the join still stands (the server arrives with
    // the `guild:create` socket event), so say so and go there anyway rather
    // than showing «couldn't join» for a join that happened.
    try {
      useDataStore.getState().upsertGuild(await api.getGuild(guildId));
    } catch {
      toast.success(t('srv.joinedServer', { name: preview?.guild.name ?? '' }));
    }
    useDataStore.getState().selectGuild(guildId);
    props.onDone();
  }

  const g = preview?.guild;
  const initial = g?.name.charAt(0).toUpperCase() ?? '?';

  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => { if (!open) props.onDone(); }}>
      <Modal.Container size="sm">
        <Modal.Dialog>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{t('srv.inviteJoinTitle')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            {loading && (
              <div className="py-8 text-center text-sm text-ink-muted">
                {t('srv.checkingInvite')}
              </div>
            )}

            {!loading && preview && g && (
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <div
                  className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl text-xl font-light tracking-[-0.02em] text-accent"
                  style={{
                    backgroundColor: 'rgb(var(--bk-accent) / 0.10)',
                    boxShadow: '0 0 20px 0 rgb(var(--bk-accent) / 0.15) inset',
                  }}
                >
                  {g.iconUrl ? (
                    <img src={g.iconUrl} alt="" className="h-full w-full rounded-2xl object-cover" />
                  ) : (
                    initial
                  )}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-base font-light tracking-[-0.015em] text-ink">
                    {g.name}
                  </div>
                  <div className="mt-0.5 text-[13px] tabular-nums text-ink-muted">
                    {t('srv.inviteMemberCount', { count: preview.memberCount })}
                  </div>
                </div>
              </div>
            )}

            {/* Always-present live region so the error is announced to screen
                readers when it's injected after pressing Join. */}
            <div aria-live="assertive" className="empty:hidden">
              {error && (
                <Alert status="danger" className="mt-1">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>{error}</Alert.Title>
                  </Alert.Content>
                </Alert>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex w-full items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={props.onDone} disabled={joining}>
                {t('srv.inviteDecline')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void join()}
                disabled={joining || loading || !preview}
              >
                {joining ? t('srv.joining') : t('srv.inviteJoinCta')}
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
